import { clamp, HintAnimation } from '../hint-motion';
import {
  abs,
  C,
  click,
  cursorTemplate,
  DIVIDER_V,
  glass,
  icon,
  mountTemplate,
  MOTION,
  nextUid,
  orbiIcon,
  orbiTemplate,
  PRESSED,
  Refs,
  renderCamera,
  renderCursor,
  ORBI_FACE_SECONDS,
  renderOrbi,
  stage,
} from './scene-kit';

/**
 * Gerüst der Portal-Hinweis-Filme A–H (Orbi, Academy, erster Agent, Testen,
 * Interface, Konversationen, Team). Dieselbe Dramaturgie wie die
 * Claude-Design-Filme 1–6, damit alle Hinweise gleich „lesen“:
 *
 *  1. WARUM  (0–1,0)  kinetische Schrift + Orbi: der Nutzen in einem Satz.
 *  2. WO     (1,0–~2) Portal erscheint, der Zeiger geht den echten Klickpfad
 *            (Beschriftungen wie im Portal), das Ziel leuchtet kurz auf.
 *  3. WAS    (~2–4,2) die Kamera rückt heran, das Ergebnis entsteht sichtbar.
 *  4. GESCHAFFT       Bestätigung (grüner Chip / Häkchen) und Orbi lacht.
 *  5. Loop   (4,4–5)  Portal blendet aus, der Satz kommt wieder.
 *
 * Bühne 640×354, Autorenzeit 5,0 s, gespielt 8,0 s (T = Abspielzeit / 1,6).
 * Ein Film ist reine Daten (`HintStorySpec`); Spuren wie in den Academy-
 * Szenen (`story-scene.ts`): `at` = Zeitpunkte, dazwischen `MOTION.path`.
 * Skripte: features/portal-hints/claude-design-skripte.md (Hinweise A–H).
 */

const STRETCH = 1.6;
const AUTHORED = 5.0;
export const CUE = { portal: 1.0, loop: 4.4 };

export interface StoryTrack {
  r: string;
  at: number[];
  o?: number[];
  x?: number[];
  y?: number[];
  s?: number[];
}

export interface HintStorySpec {
  /** Satz der Einleitung: Zeile 1 groß, Zeile 2 blau — je Wort ein Eintrag. */
  intro: { line1: string[]; line2: string[] };
  /** Inhalt der Kamera-Ebene (640×400, Weltkoordinaten). */
  portal: string;
  tracks?: StoryTrack[];
  /** Text erscheint Zeichen für Zeichen im Element `r` (mit Schreibmarke). */
  typing?: { r: string; text: string; from: number; to: number }[];
  /** Blaues Aufleuchten (Spotlight) des Elements `r` ab `at`. */
  glows?: { r: string; at: number; dur?: number }[];
  /** Orbis aus `orbiTemplate(prefix)` im Portal: Hüpfer bei `pop`, Lachen ab `laugh`. */
  orbis?: { prefix: string; pop: number; laugh: number }[];
  cursor: { at: number[]; x: number[]; y: number[]; clicks: number[] };
  camera?: { at: number[]; x: number[]; y: number[]; s: number[] };
  /** Schlussbild (Autorenzeit) für `prefers-reduced-motion` — muss allein erklären. */
  still: number;
}

export class HintStory implements HintAnimation {
  readonly width = 640;
  readonly height = 354;
  readonly playbackSeconds = AUTHORED * STRETCH;
  readonly stillAt: number;
  private refs: Refs = {};

  constructor(private spec: HintStorySpec) {
    // Das Schlussbild zeigt Orbi lachend — nicht mitten in der Kopfdrehung.
    const laughs = (spec.orbis ?? []).map((o) => o.laugh).filter((t) => t < CUE.loop);
    this.stillAt = Math.min(CUE.loop - 0.02, Math.max(spec.still, ...laughs.map((t) => t + ORBI_FACE_SECONDS + 0.02))) * STRETCH;
  }

  mount(host: HTMLElement): void {
    const words = (list: string[], ref: string) =>
      list.map((t) => `<span data-r="${ref}" style="display:inline-block;opacity:0;">${t}</span>`).join('');
    const intro =
      `<div data-r="intro" style="${abs(0, 108, 640, 130, 'display:flex;flex-direction:column;align-items:center;gap:6px;')}">` +
      `<div style="display:flex;align-items:center;gap:10px;">` +
      `<div style="display:flex;gap:9px;font-size:36px;font-weight:600;letter-spacing:-0.025em;color:${C.text};white-space:nowrap;">${words(this.spec.intro.line1, 'w1')}</div>` +
      orbiTemplate('intro', 36, 'opacity:0;') +
      `</div>` +
      `<div style="display:flex;gap:7px;font-size:24px;font-weight:600;letter-spacing:-0.02em;color:${C.action};white-space:nowrap;margin-right:46px;">${words(this.spec.intro.line2, 'w2')}</div>` +
      `</div>`;
    const portal =
      `<div data-r="portal" style="position:absolute;inset:0;opacity:0;transform-origin:320px 176px;">${this.spec.portal}</div>` +
      `<div data-r="cursorWrap" style="position:absolute;inset:0;opacity:0;">${cursorTemplate(nextUid('al-hint-story'))}</div>`;
    this.refs = mountTemplate(host, stage(portal, intro));
  }

  render(t: number): void {
    this.frame(clamp(t / STRETCH, 0, AUTHORED));
  }

  private frame(T: number): void {
    const { portal: po, loop: lp } = CUE;
    const r = this.refs;
    const live = 1 - MOTION.glide(lp, lp + 0.6, T);

    // 1 · Warum — kinetische Schrift mit Orbi
    const out = MOTION.glide(po, po + 0.3, T);
    const intro = r['intro'][0];
    intro.style.opacity = String(1 - out);
    intro.style.transform = `translateY(${-30 * out}px)`;
    const word = (el: HTMLElement, s: number) => {
      const e = MOTION.enter(s, s + 0.3, T);
      el.style.opacity = String(e);
      el.style.transform = `translateY(${(1 - e) * 10}px)`;
    };
    r['w1'].forEach((el, i) => word(el, 0.06 + 0.1 * i));
    r['w2'].forEach((el, i) => word(el, 0.4 + 0.07 * i));
    const orbiIn = MOTION.enter(0.3, 0.5, T);
    r['introWrap'][0].style.opacity = String(orbiIn);
    r['introWrap'][0].style.transform = `scale(${0.6 + 0.4 * orbiIn})`;
    renderOrbi(r, 'intro', MOTION.glide(0.36, 0.36 + ORBI_FACE_SECONDS * 0.85, T), Math.sin(Math.PI * clamp((T - 0.3) / 0.35, 0, 1)));

    // 2–4 · Portal
    const portal = r['portal'][0];
    portal.style.opacity = String(MOTION.enter(po + 0.08, po + 0.4, T) * live);
    portal.style.transform = `scale(${1.03 - 0.03 * MOTION.enter(po + 0.08, po + 0.45, T)})`;
    r['cursorWrap'][0].style.opacity = String(MOTION.enter(po + 0.1, po + 0.25, T) * live);

    for (const tr of this.spec.tracks ?? []) {
      const els = r[tr.r];
      if (!els) continue;
      const o = tr.o ? MOTION.path(tr.at, tr.o, T) : null;
      const moved = !!(tr.x || tr.y || tr.s);
      const x = tr.x ? MOTION.path(tr.at, tr.x, T) : 0;
      const y = tr.y ? MOTION.path(tr.at, tr.y, T) : 0;
      const s = tr.s ? MOTION.path(tr.at, tr.s, T) : 1;
      for (const el of els) {
        if (o !== null) {
          el.style.opacity = String(o);
          el.style.visibility = o <= 0.001 ? 'hidden' : 'visible';
        }
        if (moved) el.style.transform = `translate(${x}px, ${y}px) scale(${s})`;
      }
    }

    for (const ty of this.spec.typing ?? []) {
      const el = r[ty.r]?.[0];
      if (!el) continue;
      const n = Math.round(ty.text.length * MOTION.glide(ty.from, ty.to, T));
      const caret = T >= ty.from && T < ty.to + 0.2 ? '<span style="opacity:.7;">|</span>' : '';
      const html = escapeHtml(ty.text.slice(0, n)) + caret;
      if (el.innerHTML !== html) el.innerHTML = html;
    }

    for (const g of this.spec.glows ?? []) {
      const p = Math.sin(Math.PI * clamp((T - g.at) / (g.dur ?? 0.7), 0, 1));
      for (const el of r[g.r] ?? []) {
        el.style.boxShadow = `0 0 0 ${2.5 * p}px rgb(59 130 246 / ${0.6 * p}), 0 0 ${20 * p}px rgb(59 130 246 / ${0.45 * p})`;
      }
    }

    for (const o of this.spec.orbis ?? []) {
      renderOrbi(r, o.prefix, MOTION.glide(o.laugh, o.laugh + ORBI_FACE_SECONDS, T), Math.sin(Math.PI * clamp((T - o.pop) / 0.35, 0, 1)));
    }

    // Zeiger
    const cu = this.spec.cursor;
    const ks = cu.clicks.map((at) => click(at, T));
    const press = Math.max(0, ...ks.map((k) => k.press));
    const ring = ks.map((k) => k.ring).find((v) => v > 0 && v < 1) || 0;
    renderCursor(r['ring'][0], r['arrow'][0], MOTION.path(cu.at, cu.x, T), MOTION.path(cu.at, cu.y, T), press, ring);

    // Kamera
    const cam = this.spec.camera;
    if (cam) {
      renderCamera(r['camera'][0], MOTION.path(cam.at, cam.x, T), MOTION.path(cam.at, cam.y, T), MOTION.path(cam.at, cam.s, T));
    } else {
      renderCamera(r['camera'][0], 320, 176, 1);
    }
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ── Portal-Rahmen ────────────────────────────────────────────────────────

/** Seitenleiste: Einträge [Symbol, Beschriftung]. */
export type NavItem = [string, string];

export const NAV_MAIN: NavItem[] = [
  ['agent', 'KI-Agenten'],
  ['brain', 'Wissensbasis'],
  ['brush', 'Interface'],
  ['plug', 'Integrationen'],
  ['award', 'Academy'],
];

/** Weltkoordinaten der Seitenleisten-Zeile `i` (Mitte) — Zielpunkt des Zeigers. */
export const navPoint = (i: number): [number, number] => [85, 87 + 40 * i];

/** Ursprung des Inhaltsbereichs in Weltkoordinaten (Inhalt rechnet lokal). */
export const CONTENT = { x: 170, y: 56, w: 460, h: 284 };

/**
 * Das Portal wie im echten Layout: Kopfzeile (Wortmarke, optional Orbi-Knopf,
 * Glocke, Avatar), Seitenleiste mit eingedrückter Auswahl und Inhaltsfläche.
 * Jede Zeile hat eine Auswahl-Ebene `nav<i>` (Spur `o` wechselt die Seite)
 * und eine Leucht-Ebene `navGlow<i>`.
 */
export function portalShell(opts: { nav?: NavItem[]; active?: number; orbiButton?: boolean; content: string }): string {
  const nav = opts.nav ?? NAV_MAIN;
  const round = 'border-radius:999px;display:flex;align-items:center;justify-content:center;';
  const bubbleBtn = `background:rgb(255 255 255 / .5);box-shadow:inset 0 1px 1px rgb(255 255 255 / .9), 0 3px 8px -5px rgb(38 65 112 / .35);`;
  const header =
    `<div style="${abs(22, 14, 160, 30, `display:flex;align-items:center;font-size:18px;font-weight:700;letter-spacing:-0.035em;color:${C.text};`)}">alphalearn</div>` +
    (opts.orbiButton
      ? `<div data-r="orbiBtn" style="${abs(510, 14, 30, 30, `${round}${bubbleBtn}`)}"><div style="position:absolute;inset:5px;">${orbiIcon(20, C.text)}</div></div>`
      : '') +
    `<div style="${abs(546, 14, 30, 30, `${round}${bubbleBtn}`)}">${icon('bell', 16)}</div>` +
    `<div data-r="avatar" style="${abs(582, 14, 30, 30, `${round}font-size:11px;font-weight:600;color:${C.text};letter-spacing:0.02em;background:radial-gradient(circle at 30% 25%, rgb(255 255 255 / .9), rgb(255 255 255 / .55) 45%, rgb(180 127 255 / .35));`)}">AW</div>`;
  const rows = nav
    .map(
      ([ic, label], i) =>
        `<div style="${abs(8, 14 + 40 * i, 134, 34)}">` +
        `<div data-r="nav${i}" style="position:absolute;inset:0;border-radius:14px;opacity:${i === opts.active ? 1 : 0};${PRESSED}"></div>` +
        `<div data-r="navGlow${i}" style="position:absolute;inset:0;border-radius:14px;"></div>` +
        `<div style="position:relative;height:100%;display:flex;align-items:center;gap:9px;padding:0 10px;font-size:12.5px;font-weight:500;color:${C.text};white-space:nowrap;">${icon(ic, 16)}${label}</div></div>`,
    )
    .join('');
  const sidebar = glass(24, 0.42, 'left:10px;top:56px;width:150px;height:284px;', rows);
  const content = glass(26, 0.42, `left:${CONTENT.x}px;top:${CONTENT.y}px;width:${CONTENT.w}px;height:${CONTENT.h}px;`, opts.content);
  return header + sidebar + content;
}

/** Senkrechte Trennlinie (CI-Gravur). */
export const vline = (x: number, y: number, h: number) => `<div style="${abs(x, y, 2, h, `background:${DIVIDER_V};`)}"></div>`;

/** Leere Ebene mit Ref, z. B. als Leuchtfläche über einem Knopf. */
export const glowLayer = (ref: string, x: number, y: number, w: number, h: number, radius = 999) =>
  `<div data-r="${ref}" style="${abs(x, y, w, h, `border-radius:${radius}px;pointer-events:none;`)}"></div>`;

/** Kontur-Knopf wie `nbButton appearance="outline" status="primary"`. */
export const outlinePill = (x: number, y: number, w: number, label: string, ref = '') =>
  `<div ${ref ? `data-r="${ref}"` : ''} style="${abs(x, y, w, 30, `border-radius:999px;background:rgb(255 255 255 / .55);box-shadow:inset 0 0 0 1.5px ${C.action};color:${C.action};display:flex;align-items:center;justify-content:center;gap:5px;font-size:12px;font-weight:600;white-space:nowrap;`)}">${icon('plus', 14, C.action, 2.2)}${label}</div>`;

/** Eingabefeld (helle Pille) mit Platzhalter `ph` und Tipp-Ebene `typed`. */
export function inputField(x: number, y: number, w: number, placeholder: string, phRef: string, typedRef: string, h = 32): string {
  return (
    `<div style="${abs(x, y, w, h, `border-radius:999px;background:rgb(255 255 255 / .6);box-shadow:inset 0 4px 6px -3px rgb(47 76 121 / .23), inset 0 -4px 6px -3px rgb(255 255 255 / .90);`)}">` +
    `<div data-r="${phRef}" style="${abs(14, 0, w - 28, h, `display:flex;align-items:center;font-size:12px;color:${C.muted};white-space:nowrap;overflow:hidden;`)}">${placeholder}</div>` +
    `<div data-r="${typedRef}" style="${abs(14, 0, w - 28, h, `display:flex;align-items:center;font-size:12.5px;font-weight:500;color:${C.text};white-space:pre;overflow:hidden;`)}"></div>` +
    `</div>`
  );
}
